<template>
  <div>
    <VClientTable
      :class="{'hide-filters': hideFilters}"
      :data="data"
      :columns="columnList"
      :options="tableOptions"
      ref="vTable"
    >
      <template #afterFilter>
        <div class="after-filter-wrapper">
          <div class="d-flex justify-content-start align-items-center w-100">
            <select
              v-if="!hideFilters"
              class="form-control"
              :value="perPage"
              @change="onPerPageChange"
            >
              <option v-for="option in perPageOptions" :key="option" :value="option">
                {{ option }}
              </option>
            </select>
            <input
              v-if="!hideFilters"
              type="text"
              class="VueTables__search__input form-control"
              :placeholder="tableOptions.texts.filterPlaceholder"
              :value="searchQuery"
              @input="onSearchInput"
            />
            <div class="after-filter-actions">
              <button v-if="showSelect" class="search-button" @click="editAction()" type="button">
                {{editButtonText}}
              </button>
              <div v-if="showDownload" style="height: 45px;">
                <img src="../../assets/images/icons/excel-export-H.svg" class="dwn-button" @click="startDownload"/>
              </div>
            </div>
            <el-select
              v-if="!hideFiltersList"
              v-model="visibleColumns"
              collapse-tags
              multiple
            >
              <el-option
                v-for="column in selectableColumnList"
                :key="parseMetricName(column)"
                :label="parseMetricName(column)"
                :value="column"
              >
              </el-option>
            </el-select>
          </div>
        </div>
      </template>
      <template #c_mi_number="{ row }">
        <a v-if="row.c_mi_number !== '-'" :href="`/study/${row.id}/`" target="_blank"><span>{{ row.c_mi_number }}</span></a>
        <span v-else>{{ row.c_mi_number }}</span>
      </template>
      <template v-for="column in multivalueColumns" v-slot:[column]="props">
        <div
          :key="column"
          class="column-tag-wrapper"
          :class="{ 'multivalue-highlight': props.row[column] && props.row[column].length > 1 }"
          v-if="Array.isArray(props.row[column])"
        >
          <span
            v-for="value in toggledFields[`${column}-${props.row.id}`] ? props.row[column] : props.row[column].slice(0, 2)"
            :key="`${column}-${props.row.id}-${value}`"
          >
            {{ value }}
          </span>
          <span
            v-if="!props.row[column].length"
            :key="`${column}-${props.row.id}-empty`"
          >
            -
          </span>
        </div>
        <span v-else :key="`${column}-${props.row.id}-single`" :class="{ 'multivalue-highlight': props.row[column] && props.row[column].length > 1 }">
          {{props.row[column]}}
        </span>
        <span
          class="field-toggle"
          v-if="Array.isArray(props.row[column]) && props.row[column].length > 2"
          :key="`${column}-${props.row.id}-toggle`"
          @click.stop="toggleField(`${column}-${props.row.id}`)"
        >
          <i>{{ toggledFields[`${column}-${props.row.id}`] ? 'less' : `(${props.row[column].length - 2}) more` }}</i>
        </span>
      </template>
      <template #selection="{ row }">
        <div class="checkbox-container">
          <Checkbox
            :key="`checkbox-${row.id}`"
            :value="Boolean(selectedRows[row.id])"
            @change="(val) => changeSelectedCount(row.id, val)">
          </Checkbox>
        </div>
      </template>
      <template #metric_name="{ row }">
        <span>{{ row.metric_name }}{{ row.is_binary_scale ? '*' : ''}}</span>
      </template>
      <template #links="{ row }">
        <div class="d-flex flex-column">
          <router-link :to="`/study/${row.id}/structure/`" target="_blank">
            files
          </router-link>
          <router-link :to="`/study/${row.id}/`" target="_blank">
            details
          </router-link>
          <router-link :to="{ name: 'rdf_upload_id', params: { id: row.id }}" target="_blank">
            statistics
          </router-link>
        </div>
      </template>
      <template #file_name="{ row }">
        <div class="file-data d-flex justify-content-center align-items-center" @click="startDownload(row)" style="cursor: pointer;">
          <span style="text-align: center; width: 100%">{{ row.file_name }}</span>
          <font-awesome-icon class="file-icon ml-2"
                             :icon="getFileIcon(row.file_name)"></font-awesome-icon>
        </div>
      </template>
    </VClientTable>
  </div>
</template>

<script>
import Checkbox from './Checkbox'
import VClientTable from './VClientTable'
export default {
  name: 'Table',
  components: { Checkbox, VClientTable },
  props: {
    data: Array,
    columns: Array,
    options: Object,
    additionalColumns: {
      type: Array,
      default: () => []
    },
    isSelectionActive: Boolean,
    showChildRows: Boolean,
    showDownload: Boolean,
    showSelect: Boolean,
    hideFilters: Boolean,
    hideFiltersList: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      perPage: 10,
      perPageOptions: [10, 25, 50, 100],
      searchQuery: '',
      visibleColumns: [],
      toggledFields: {},
      selectedRows: {},
      selectedRowsCount: 0,
      multivalueColumns: [
        'client',
        'category',
        'product_type',
        'business_unit',
        'region',
        'country',
        'design_method',
        'research_type',
        'methodology',
        'cmi_tool_type',
        'evaluation'
      ],
      columnsDrawerToggled: false
    }
  },
  computed: {
    tableOptions () {
      return {
        ...this.options,
        perPage: this.perPage,
        perPageDropdown: this.perPageOptions,
        visibleColumns: this.allVisibleColumns,
        resizableColumns: true,
        columnsDropdown: false,
        sortable: [...this.columns, ...this.additionalColumns],
        texts: {
          filterPlaceholder: 'Search query within the label displayed'
        },
        sortIcon: {
          is: 'fa-sort',
          base: 'font-awesome-icon fas',
          up: 'fa-sort-up',
          down: 'fa-sort-down'
        },
        cellClasses: this.cellClassOptions
      }
    },
    allVisibleColumns () {
      let allColumns = this.visibleColumns
      if (this.isSelectionActive) {
        allColumns = ['selection', ...allColumns]
      }
      return allColumns
    },
    cellClassOptions () {
      const classes = {}
      this.multivalueColumns.forEach(column => {
        classes[column] = [{ class: 'multivalue-highlight', condition: row => row[column].length > 1 }]
      })
      return classes
    },
    selectableColumnList () {
      return [...this.columns, ...this.additionalColumns]
    },
    columnList () {
      let allColumns = [...this.columns, ...this.additionalColumns]
      if (this.isSelectionActive) {
        allColumns = ['selection', ...allColumns]
      }
      return allColumns
    },
    editButtonText () {
      return this.isSelectionActive
        ? this.selectedRowsCount
          ? `Edit ${this.selectedRowsCount} stud${this.selectedRowsCount > 1 ? 'ies' : 'y'}`
          : 'Cancel'
        : 'Edit'
    }
  },
  methods: {
    onPerPageChange (event) {
      const value = Number(event.target.value)
      this.perPage = Number.isNaN(value) ? this.perPageOptions[0] : value
    },
    onSearchInput (event) {
      this.searchQuery = event.target.value
      if (this.$refs.vTable && this.$refs.vTable.setFilter) {
        this.$refs.vTable.setFilter(this.searchQuery)
      }
    },
    parseMetricName (value) {
      switch (value) {
        case 'c_mi_number':
          return 'CMI #'
        case 'ref_number':
          return 'Developer #'
        case 'code':
          return '6 Digit Code (SAP)'
        default:
          const data = value.replace(/_/g, ' ')
          return data.charAt(0).toUpperCase() + data.substring(1)
      }
    },
    startDownload (props) {
      this.$emit('download', props)
    },
    setQuery (val) {
      this.$refs.vTable.setFilter(val)
    },
    toggleField (tag) {
      this.$set(this.toggledFields, tag, !this.toggledFields[tag])
    },
    changeSelectedCount (rowId, boolVal) {
      this.$set(this.selectedRows, rowId, boolVal)
      this.selectedRowsCount = Object.values(this.selectedRows).filter(b => b).length
    },
    editAction () {
      this.$emit('select', this.getSelectEventType())
      this.initializeSelectedRows()
      this.selectedRowsCount = 0
    },
    getSelectEventType () {
      if (!this.isSelectionActive) {
        return { type: 'edit' }
      } else if (!this.selectedRowsCount) {
        return { type: 'cancel' }
      } else {
        return { type: 'confirmEdit', data: this.getSelectedData() }
      }
    },
    getSelectedData () {
      const selectedData = []
      for (const [studyId, isStudyToggled] of Object.entries(this.selectedRows)) {
        if (isStudyToggled) {
          const studyDataObj = this.data.find(el => String(el.id) === String(studyId))
          if (studyDataObj) {
            selectedData.push(studyDataObj)
          }
        }
      }
      return selectedData
    },
    initializeSelectedRows () {
      this.data.forEach(el => {
        this.selectedRows[el.id] = false
      })
    },
    getFileIcon (fileName) {
      if (!fileName) {
        return 'file-alt'
      }
      const type = fileName.split('.').pop()
      switch (type) {
        case 'doc':
          return 'file-word'
        case 'docx':
          return 'file-word'
        case 'ppt':
          return 'file-powerpoint'
        case 'pptx':
          return 'file-powerpoint'
        case 'pdf':
          return 'file-pdf'
        case 'xlsx':
          return 'file-excel'
        case 'xls':
          return 'file-excel'
        default:
          return 'file-alt'
      }
    }
  },
  beforeMount () {
    this.visibleColumns = [...this.columns]
    this.initializeSelectedRows()
  }
}
</script>

<style lang="scss">
@import "../../assets/scss/variables";

.fp-row-selected {
  * {
    color: #693896 !important;
  }
}

.multivalue-highlight {
  background-color: $soft-blue;
}

tr:nth-of-type(odd) {
  .multivalue-highlight {
    background-color: #E5E6ED;
  }
}

tr:hover {
  .multivalue-highlight {
    background-color: #e9eaf1;
  }
}

.column-tag-wrapper {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.field-toggle {
  cursor: pointer;
  color: #20a8d8;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}

.search-button {
  width: 230px;
  height: 48px;
  background-color: #300296;
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  font-weight: 800;
  line-height: 30px;
  letter-spacing: 0;
  border-radius: 4px;
  color: white;
  text-transform: uppercase;
  transition: background-color 0.2s linear;

  &:hover {
    color: white;
    background-color: #9256D9;
  }
}

.checkbox-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.VueTables > .row > .col-md-12 {
  display: flex;
  flex-direction: row-reverse;
}

.VueTables {
  td {
    vertical-align: middle !important;
  }
}

.filter-input {
  width: 250px !important;
  margin-right: auto;
  margin-bottom: 10px;
}

.hide-filters {
  .VueTables__search-field {
    display: none !important;
  }
  .vgt-global-search {
    display: none !important;
  }
  .after-filter-wrapper {
    width: 100%;
    justify-content: flex-end;
    .info-text {
      margin-right: 15px;
    }
  }
}

.search-button {
  overflow: hidden;
  white-space: nowrap;
  border-radius: 5px;
}

.el-checkbox-button.el-checkbox-button--small.is-checked > .el-checkbox-button__inner {
  background-color: #20a8d8;
  border-color: #20a8d8;
}

.VueTables__search {
  width: 100%;
  margin-bottom: 0;
}

.VueTables__search-field {
  margin-right: auto;
}

.VueTables__sort-icon {
  margin-left: 8px;
}

.VueTables__search__input {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: 0;
  border-radius: 4px !important;
  border: 2px solid $pewter !important;
  height: 40px !important;
  width: 360px !important;
}

.vgt-global-search {
  width: 100%;
  margin-bottom: 0;
}

.vgt-global-search__input .vgt-input {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: 0;
  border-radius: 4px !important;
  border: 2px solid $pewter !important;
  height: 40px !important;
  width: 360px !important;
}

.VueTables__limit-field {
  margin-right: 10px;
}

.VueTables__columns-dropdown-wrapper {
  margin: auto 20px auto 0;
}

.VuePagination nav {
}

.VuePagination.row.col-md-12 {
  flex: 0 !important;
}

.VuePagination {
  nav {
    justify-content: space-between;
  }
  &__count {
    width: 300px;
  }
}

.VueTables__limit-field {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: 0;

  select {
    border: 2px solid $pewter !important;
    height: 40px !important;
  }
}

.VueTables__search-field {
  label {
    display: none !important;
  }
}

.VueTables__limit {
  margin: 0;
  height: 51px;

  label {
    display: none !important;
  }

  select {
    border-radius: 4px;
  }
}

.file-data {
  width: 100%;
}

.file-data:hover {
  color: #20a8d8;
}

.file-icon {
  font-size: 1.5rem;
  float: right;
}

.after-filter-wrapper {
  padding-left: 0;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  .dwn-button {
    cursor: pointer;
    width: 65px;
    height: 65px;
    margin-right: 15px;
    margin-top: -10px;
  }

  .info-text {
    font-size: 12px;
    font-family: 'Source Sans Pro', sans-serif;
    color: $pewter;
  }
}

.after-filter-wrapper .el-select {
  width: 360px;
  max-width: 360px;
}

.after-filter-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
}

.after-filter-wrapper .VueTables__search__input {
  margin-right: 15px;
  flex: 0 0 360px;
}

.after-filter-wrapper select.form-control {
  margin-right: 15px;
  width: 120px;
  flex: 0 0 120px;
}

.after-filter-wrapper + .vgt-wrap .vgt-global-search {
  display: none;
}
</style>
